import React, { useState, useEffect, } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Collapse,
  Divider,
  Drawer,
  TextField,
  Typography
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { DateRange } from 'components';
import { formatBookingStatus, getRestrictedParkings, } from 'helpers/helpers'
import { CANCELED, WAIT_ACCEPT, BOOKED, } from 'constants/bookingStatus'
import { fetchParkings, } from 'firebaseHelpers/parkings'

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  drawer: {
    width: 420,
    maxWidth: '100%'
  },
  header: {
    padding: theme.spacing(2, 1),
    display: 'flex',
    justifyContent: 'space-between'
  },
  buttonIcon: {
    marginRight: theme.spacing(1)
  },
  content: {
    padding: theme.spacing(0, 3),
    flexGrow: 1
  },
  contentSection: {
    padding: theme.spacing(2, 0)
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  contentSectionContent: {},
  formGroup: {
    padding: theme.spacing(2, 0)
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center'
  },
  field: {
    marginTop: 0,
    marginBottom: 0
  },
  flexGrow: {
    flexGrow: 1
  },
  addButton: {
    marginLeft: theme.spacing(1)
  },
  tags: {
    marginTop: theme.spacing(1)
  },
  minAmount: {
    marginRight: theme.spacing(3)
  },
  maxAmount: {
    marginLeft: theme.spacing(3)
  },
  radioGroup: {},
  actions: {
    padding: theme.spacing(3),
    '& > * + *': {
      marginTop: theme.spacing(2)
    }
  },
}));

const Filter = props => {
  const { open, onClose, onFilter, className, ...rest } = props;

  const classes = useStyles();

  const initialValues = {
    userLastName: '',
    userFirstName: '',
    userEmail: '',
    status: '',
    parkingId: '',
    reference: '',
    fromTo: { startDate: '', endDate: '' },
    numberPlate: '',
  };

  const session = useSelector(state => state.session);
  const [expandBooking, setExpandBooking] = useState(true);
  const [expandUser, setExpandUser] = useState(true);
  const [values, setValues] = useState({ ...initialValues });
  const [parkings, setParkings] = useState([])

  useEffect(() => {
    let isMounted = true
    const restrictedParkings = getRestrictedParkings(session.user)

    const fetchData = async () => {
      const parkings = await fetchParkings(null, restrictedParkings)
      if (isMounted) {
        setParkings(parkings)
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [session.user])

  const handleClear = () => {
    setValues({ ...initialValues });
  };

  const handleFieldChange = (event, field, value) => {
    event.persist && event.persist();
    setValues(values => ({
      ...values,
      [field]: value
    }));
  };

  const handleToggleUser = () => {
    setExpandUser(expandUser => !expandUser);
  };

  const handleToggleBooking = () => {
    setExpandBooking(expandBooking => !expandBooking);
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (onFilter) {
      let countItems = 0
      const updatedValues = Object.keys(values).reduce((acc, key) => {
        if (values[key] !== '') {
          countItems++
          return { ...acc, [key]: values[key], }
        }
        return acc
      }, {})
      onClose()
      onFilter(countItems ? updatedValues : null)
    }
  };

  const setFilterDate = (label, date) => {
    setValues(values => ({
      ...values,
      [label]: { ...values[label], ...date },
    }));
  }

  const statusOptions = [ CANCELED, WAIT_ACCEPT, BOOKED ];

  return (
    <Drawer
      anchor="right"
      classes={{ paper: classes.drawer }}
      onClose={onClose}
      open={open}
      variant="temporary"
    >
      <form
        {...rest}
        className={clsx(classes.root, className)}
        onSubmit={handleSubmit}
      >
        <div className={classes.header}>
          <Button
            onClick={onClose}
            size="small"
          >
            <CloseIcon className={classes.buttonIcon} />
            Close
          </Button>
        </div>
        <div className={classes.content}>
          <div className={classes.contentSection}>
            <div
              className={classes.contentSectionHeader}
              onClick={handleToggleBooking}
            >
              <Typography variant="h5">Booking</Typography>
              {expandBooking ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
            <Divider />
            <Collapse in={expandBooking}>
              <div className={classes.contentSectionContent}>
                <div className={classes.contentSectionContent}>
                  <div className={classes.formGroup}>
                    <TextField
                      className={classes.field}
                      fullWidth
                      label="Status"
                      margin="dense"
                      name="status"
                      onChange={event =>
                        handleFieldChange(
                          event,
                          'status',
                          event.target.value
                        )
                      }
                      select
                      // eslint-disable-next-line react/jsx-sort-props
                      SelectProps={{ native: true }}
                      value={values.status}
                      variant="outlined"
                    >
                      <option
                        disabled
                        value=""
                      />
                      {statusOptions.map(option => (
                        <option
                          key={option}
                          value={option}
                          label={formatBookingStatus(option)}
                        >
                          {option}
                        </option>
                      ))}
                    </TextField>
                  </div>
                  <div className={classes.formGroup}>
                    <TextField
                      className={classes.field}
                      fullWidth
                      label="Parking"
                      margin="dense"
                      name="parkingId"
                      onChange={event =>
                        handleFieldChange(
                          event,
                          'parkingId',
                          event.target.value
                        )
                      }
                      select
                      // eslint-disable-next-line react/jsx-sort-props
                      SelectProps={{ native: true }}
                      value={values.parkingId}
                      variant="outlined"
                    >
                      <option
                        disabled
                        value=""
                      />
                      {parkings.map(option => (
                        <option
                          key={option.id}
                          value={option.id}
                          label={option.name}
                        >
                          {option.name}
                        </option>
                      ))}
                    </TextField>
                  </div>
                  <div className={classes.formGroup}>
                    <TextField
                      className={classes.field}
                      fullWidth
                      label="Booking Reference"
                      margin="dense"
                      name="reference"
                      onChange={event =>
                        handleFieldChange(
                          event,
                          'reference',
                          event.target.value
                        )
                      }
                      value={values.reference}
                      variant="outlined"
                    />
                  </div>
                  <div className={classes.formGroup}>
                    <DateRange values={values.fromTo} label="fromTo" setValues={setFilterDate}/>
                  </div>
                  <div className={classes.formGroup}>
                    <TextField
                      className={classes.field}
                      fullWidth
                      label="Number Plate"
                      margin="dense"
                      name="numberPlate"
                      onChange={event =>
                        handleFieldChange(
                          event,
                          'numberPlate',
                          event.target.value
                        )
                      }
                      value={values.numberPlate}
                      variant="outlined"
                    />
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
          <div className={classes.contentSection}>
            <div
              className={classes.contentSectionHeader}
              onClick={handleToggleUser}
            >
              <Typography variant="h5">User</Typography>
              {expandUser ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
            <Divider />
            <Collapse in={expandUser}>
              <div className={classes.formGroup}>
                <TextField
                  className={classes.field}
                  fullWidth
                  label="First Name"
                  margin="dense"
                  name="userFirstName"
                  onChange={event =>
                    handleFieldChange(
                      event,
                      'userFirstName',
                      event.target.value
                    )
                  }
                  value={values.userFirstName}
                  variant="outlined"
                />
              </div>
              <div className={classes.formGroup}>
                <TextField
                  className={classes.field}
                  fullWidth
                  label="Last Name"
                  margin="dense"
                  name="userLastName"
                  onChange={event =>
                    handleFieldChange(
                      event,
                      'userLastName',
                      event.target.value
                    )
                  }
                  value={values.userLastName}
                  variant="outlined"
                />
              </div>
              <div className={classes.formGroup}>
                <TextField
                  className={classes.field}
                  fullWidth
                  label="Email"
                  margin="dense"
                  name="userEmail"
                  onChange={event =>
                    handleFieldChange(
                      event,
                      'userEmail',
                      event.target.value
                    )
                  }
                  value={values.userEmail}
                  variant="outlined"
                />
              </div>
            </Collapse>
          </div>
        </div>
        <div className={classes.actions}>
          <Button
            fullWidth
            onClick={handleClear}
            variant="contained"
          >
            <DeleteIcon className={classes.buttonIcon} />
            Clear
          </Button>
          <Button
            color="primary"
            fullWidth
            type="submit"
            variant="contained"
          >
            Apply filters
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

Filter.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onFilter: PropTypes.func,
  open: PropTypes.bool.isRequired
};

export default Filter;
